// Sprint-009 B5 — weather copy + icon mapping (design-phase-b.md §3). Every
// string here is checked against `design-spec-today-card.md` §4's banned
// list plus this spec's own additions (§8: never "stale," "cache expired,"
// "not configured," "coordinates missing"). `condition_label`/temperatures
// are always the SERVER's own rendered text (§3.5) — this file only builds
// the "as of"/staleness sentence and maps a decorative glyph; it never
// re-derives or overrides the condition label itself.

import type { DailyForecastOut } from "../types";

// ---------------------------------------------------------------------------
// §3.2 — six-glyph, purely decorative icon set (always `aria-hidden`). The
// SET is the design decision, not this exact WMO-code -> bucket mapping
// (§3.2's own framing) — grouped from the Open-Meteo/WMO weather-code table
// the backend adapter uses (app/weather/providers/open_meteo.py).
// ---------------------------------------------------------------------------

const CLEAR_CODES = new Set([0, 1]);
const PARTLY_CLOUDY_CODES = new Set([2]);
// Overcast + fog/mist codes fold into the same "cloudy" glyph — the
// six-bucket set has no dedicated fog glyph, and overcast is the nearer
// visual match of the two.
const OVERCAST_CODES = new Set([3, 45, 48]);
const RAIN_CODES = new Set([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82]);
const SNOW_CODES = new Set([71, 73, 75, 77, 85, 86]);
const THUNDERSTORM_CODES = new Set([95, 96, 99]);

/** `condition_code` (raw WMO) -> a decorative emoji glyph. Never rendered
 * without an adjacent `condition_label` text node — always `aria-hidden`
 * at every call site (§3.2/§3.5). Falls back to the overcast glyph for any
 * code outside the mapped set (a future provider quirk should never throw
 * or render nothing). */
export function weatherIconGlyph(conditionCode: number): string {
  if (CLEAR_CODES.has(conditionCode)) return "☀️";
  if (PARTLY_CLOUDY_CODES.has(conditionCode)) return "⛅";
  if (RAIN_CODES.has(conditionCode)) return "🌧️";
  if (THUNDERSTORM_CODES.has(conditionCode)) return "⛈️";
  if (SNOW_CODES.has(conditionCode)) return "❄️";
  if (OVERCAST_CODES.has(conditionCode)) return "☁️";
  return "☁️";
}

// ---------------------------------------------------------------------------
// §3.3 — as-of / stale / unavailable copy.
// ---------------------------------------------------------------------------

const TIME_FORMATTER = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });

/** "Updated at 1:40 PM." / "Updated at 1:40 PM — may be a little out of
 * date." (§3.3's table, verbatim) — `asOf` is an ISO instant
 * (`WeatherOut.as_of`), never null when this is called (callers only
 * reach this once `available && current` are both known). */
export function weatherAsOfLabel(asOf: string, stale: boolean): string {
  const time = TIME_FORMATTER.format(new Date(asOf));
  return stale ? `Updated at ${time} — may be a little out of date.` : `Updated at ${time}.`;
}

export const WEATHER_UNAVAILABLE_TEXT = "Forecast for the farm isn't set up yet.";
export const WEATHER_LOADING_TEXT = "Loading the forecast…";

// ---------------------------------------------------------------------------
// §3.1's 7-day forecast row — day label ("Mon"/"Tue"), no new date
// convention (reuses `lib/calendarDates.ts::weekdayShortLabel` verbatim).
// ---------------------------------------------------------------------------

export function forecastDayLabel(
  day: DailyForecastOut,
  weekdayShortLabel: (d: Date) => string,
): string {
  return weekdayShortLabel(new Date(`${day.date}T00:00:00`));
}

/** §3.4's Today-header phone strip: `"☀️ 88° · Partly cloudy"` — a plain,
 * one-line sentence, glyph included inline (still individually
 * `aria-hidden` at render time, this just builds the numeric/label part). */
export function weatherStripTempLabel(tempF: number): string {
  return `${Math.round(tempF)}°`;
}

// ---------------------------------------------------------------------------
// Sprint-012 D1 — dashboard weather-card v2 (design spec item D, B2-gated
// RC-D1). Always-visible current-conditions meta line plus daily low +
// always-on precipitation, and a plain-language external link to the
// farm's full NWS point forecast. `wind_mph`/`humidity_pct`/`daily[].low_f`
// were already on `WeatherOut` and simply unrendered before this sprint —
// no new API call.
// ---------------------------------------------------------------------------

/** Always-visible current-conditions meta line ("Wind 8 mph · Humidity
 * 54%"). Plain weather words, not jargon — no translation needed. */
export function currentMetaLabel(windMph: number, humidityPct: number): string {
  return `Wind ${Math.round(windMph)} mph · Humidity ${Math.round(humidityPct)}%`;
}

/** Daily precipitation chance, now always rendered (was previously hidden
 * at 0% — a 0% is itself useful information to a farmer, not noise to
 * hide). "% rain" (not a bare "%") so the number reads unambiguously once
 * it's no longer paired only with visibly-rainy days. */
export function precipChanceLabel(precipChancePct: number): string {
  return `${Math.round(precipChancePct)}% rain`;
}

/** Sprint-018 E5 (#55), superseding RC-D1's hardcoded NWS URL — the "Full
 * forecast" link now points at Wunderground's coordinate-form forecast
 * page (`https://www.wunderground.com/weather/{lat},{lon}`), built
 * CLIENT-SIDE from the farm's own `WeatherOut.latitude`/`longitude`
 * (PM-verified canonical form; always tracks the farm's configured point
 * rather than a hardcoded city slug). No backend change — the coordinates
 * are already on the payload. Returns `null` when either coordinate is
 * unset (farm point not configured) so the caller can omit the link
 * entirely rather than ship a broken/generic href — same "unavailable
 * affordance never renders half-broken" convention the rest of this card
 * already follows (see `WeatherMapPanel`'s own `latitude !== null &&
 * longitude !== null` gate in `DashboardView`). */
export function weatherFullForecastUrl(
  latitude: number | null,
  longitude: number | null,
): string | null {
  if (latitude === null || longitude === null) return null;
  return `https://www.wunderground.com/weather/${latitude},${longitude}`;
}

/** Plain label, no "NWS"/"forecast.weather.gov" jargon in the visible
 * text; the ↗ glyph stays decorative/`aria-hidden`. Per the B2 UX
 * Researcher's recommendation (this is the app's first external link, and
 * the admin is non-technical), the "(opens in a new tab)" cue is rendered
 * VISIBLE on screen, not screen-reader-only, so a sighted user gets the
 * same heads-up a screen-reader user gets before the tap leaves the app. */
export const WEATHER_FULL_FORECAST_LABEL = "See the full forecast";
export const WEATHER_FULL_FORECAST_NEW_TAB_HINT = "(opens in a new tab)";

// ---------------------------------------------------------------------------
// ADR-weather-map-provider-2026-07-10, keyless phase 1 — severe-weather
// alerts banner (dashboard weather card only). SA Amendment 1 condition 1
// (BINDING): the banner this copy feeds renders independently of the
// forecast's `available` flag — nothing here depends on forecast state
// either.
// ---------------------------------------------------------------------------

export type AlertTone = "warning" | "watch";

const SEVERE_ALERT_SEVERITIES = new Set(["Extreme", "Severe"]);

/** NWS `severity` (Extreme/Severe/Moderate/Minor/Unknown) collapsed to the
 * two visual/semantic tones the banner supports: "warning" (Extreme/
 * Severe — `--clay`, `role="alert"`) vs "watch" (everything else,
 * including an unrecognized future NWS value — `--amber-deep`,
 * `role="status"`). Fails safe toward the calmer tone for anything not
 * explicitly Extreme/Severe, matching `weatherIconGlyph`'s own "never
 * throw on an unmapped value" posture above. */
export function alertSeverityTone(severity: string): AlertTone {
  return SEVERE_ALERT_SEVERITIES.has(severity) ? "warning" : "watch";
}

/** Non-color-alone text label paired with `alertSeverityTone` — every
 * alert renders this alongside its `--clay`/`--amber-deep` styling so the
 * severity signal never depends on color perception alone. */
export function alertToneLabel(tone: AlertTone): string {
  return tone === "warning" ? "Warning" : "Watch / advisory";
}

/** True when at least one alert is Extreme/Severe — drives the banner's
 * own `role="alert"` (assertive) vs `role="status"` (polite) choice. */
export function anyAlertIsSevere(alerts: { severity: string }[]): boolean {
  return alerts.some((alert) => alertSeverityTone(alert.severity) === "warning");
}

// ---------------------------------------------------------------------------
// ADR-weather-map-provider-2026-07-10 Phase 2 — dashboard radar map
// (`WeatherMapPanel`, lazy-loaded Leaflet). Collapsed by default; expanded
// state remembered in `localStorage` under this key.
// ---------------------------------------------------------------------------

export const WEATHER_RADAR_EXPANDED_STORAGE_KEY = "hayloft.weatherRadarExpanded";

export const WEATHER_RADAR_SHOW_LABEL = "Show radar";
export const WEATHER_RADAR_HIDE_LABEL = "Hide radar";
/** Shown in place of the toggle entirely — the farm's point is unknown
 * (`WeatherOut.latitude`/`longitude` null), so there is nothing to center
 * a map on yet. */
export const WEATHER_RADAR_UNAVAILABLE_TEXT = "Radar map isn't set up yet.";
/** The whole panel failed to come up (Leaflet itself, or its tile
 * layers, never mounted) — the rarer, harder failure. */
export const WEATHER_RADAR_MAP_LOAD_ERROR_TEXT = "Couldn't load the radar map.";
/** The map itself came up fine (base OSM tiles are showing) but
 * RainViewer's keyless frame-index fetch failed — precip overlay only,
 * not a whole-panel failure (RainViewer/IEM offer no data-availability
 * SLA — ADR "Consequences" trade-off). */
export const WEATHER_RADAR_PRECIP_UNAVAILABLE_TEXT =
  "Precipitation imagery isn't available right now.";

/** RainViewer's own required attribution text + IEM/OSM credit — the
 * ADR's Compliance section: "mandatory attribution" is a ToS condition of
 * the RainViewer free tier, not just a nicety. Rendered inside Leaflet's
 * own attribution control (`map.attributionControl`), never omitted. */
export const WEATHER_RADAR_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors | ' +
  'Weather data by <a href="https://www.rainviewer.com" target="_blank" rel="noopener noreferrer">RainViewer</a> | ' +
  'NEXRAD: <a href="https://mesonet.agron.iastate.edu" target="_blank" rel="noopener noreferrer">Iowa Environmental Mesonet</a>';

// ---------------------------------------------------------------------------
// Sprint-015 C1/C2/C3 (weather-hub-ia-layout-2026-07-10.md §2/§3/§4, CD
// sign-off 2026-07-10) — heat-stress advisory pill/hero + garden water
// chip copy. `advisory.headline`/`garden.detail` are the SERVER's own
// plain-language strings (render verbatim, never re-derived or hardcoded
// here — spec §7 item 6). This file only translates the per-species
// `tier` enum (never rendered raw, §14 display-map discipline) and builds
// the `garden.state` -> copy mapping (undefined in the ADR/C0 spec; CD
// sign-off note 2's copy-pass proposal, not yet a final content pass).
// ---------------------------------------------------------------------------

/** `AdvisorySpeciesOut.tier` ("none|mild|moderate|severe") -> a plain-
 * language label — never the raw enum reaches the UI (species breakdown
 * list + hero species chips both pair this text with their color-dot,
 * WCAG 1.4.1 color-never-alone). Fails safe toward the calmest label for
 * anything unrecognized, matching this file's other enum-mapping
 * functions' "never throw on an unmapped value" posture. */
export function speciesTierLabel(tier: string): string {
  switch (tier) {
    case "severe":
      return "High risk";
    case "moderate":
      return "Elevated risk";
    case "mild":
      return "Caution";
    default:
      return "Low risk";
  }
}

/** True for `advisory.level === "elevated"` — drives `.heat-pill.is-
 * moderate` (amber-deep) vs the base `.heat-pill` (clay, High) tone (C1's
 * state table). */
export function heatPillIsModerate(level: string): boolean {
  return level === "elevated";
}

/** `garden.state` ("water"|"skip"|"unknown") -> the chip's own plain-
 * language line — no raw ET0/precipitation number ever surfaces here, by
 * design (§4: "No raw ET/ET0 number, ever"). The `"unknown"` copy is CD
 * sign-off note 2's proposal ("not enough data yet"), a copy-pass
 * candidate, not yet a final content decision. */
export function gardenChipLabel(state: string): string {
  switch (state) {
    case "water":
      return "Garden likely needs water today";
    case "skip":
      return "Garden's had enough rain — skip today";
    default:
      return "Not enough data yet to tell if the garden needs water";
  }
}

// ---------------------------------------------------------------------------
// Sprint-015 C4 (weather-hub-ia-layout-2026-07-10.md §5, CD sign-off
// 2026-07-10 Deviation 1 ruling — BINDING). `WeatherOut.map_base_layer` is
// the farm-wide admin default (`farm_settings.map_base_layer`, set on
// Admin > Settings) — read-only here, never a per-user preference. The
// mock's own `.layer-toggle-note` caption ("Persists as your default
// (per-user, server-side)") is superseded; this is the CD-approved
// descriptive (not instructional) replacement register. Deliberately does
// NOT embed the actual "map"/"satellite" value — the CD-approved copy is
// value-agnostic, same string regardless of which layer is the default.
// ---------------------------------------------------------------------------

export const WEATHER_RADAR_DEFAULT_LAYER_CAPTION =
  "Showing the farm's default map view. The farm admin sets this in Farm settings.";
